import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { CostCenterPriceModel } from 'src/app/models/archive/cost-center-price.model';
import { TransferConstructionSiteDetailModel } from 'src/app/models/rental/transfer-construction-site-detail.model';
import { TransferConstructionSiteModel } from 'src/app/models/rental/transfer-construction-site.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CostCenterPriceService } from 'src/app/services/archive/cost-center-price.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { TransferConstructionSiteDetailService } from 'src/app/services/rental/transfer-construction-site-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-transfer-construction-site-detail',
  templateUrl: './register-transfer-construction-site-detail.component.html',
  styleUrls: ['./register-transfer-construction-site-detail.component.scss'],
  standalone: false
})
export class RegisterTransferConstructionSiteDetailComponent implements OnChanges {

  @Input() accessCostCenterPrice: boolean = false;
  @Input() costCenterPriceContract: CostCenterPriceModel = new CostCenterPriceModel();
  @Input() disabled: boolean = false;
  @Input() transferConstructionSiteContract: TransferConstructionSiteModel = new TransferConstructionSiteModel();
  @Input() transferConstructionSiteDetailContract: TransferConstructionSiteDetailModel = new TransferConstructionSiteDetailModel();
  @Input() transferConstructionSiteDetails: TransferConstructionSiteDetailModel[] = [];
  @Output() conceptInvoicingIdEmit = new EventEmitter<number>();
  @Output() costCenterPriceRegister = new EventEmitter<CostCenterPriceModel>();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  baseUnitValue: number | null = 0;
  disableButtonSave: boolean = false;
  codeNameArticle: string = '';
  codeStore: string = '';
  rentalInvoicingConceptId: number = 0;
  formTransferConstructionSiteDetail: FormGroup = new FormGroup({});
  rateDiscount: number | null = 0;
  stock: number | null = 0;
  storeId: number = 0;
  transferConstructionSiteDetailId: number = 0;
  unitValueClient: number | null = 0;

  constructor(private articleService: ArticleService,
    private conceptInvoicingService: ConceptInvoicingService,
    private costCenterPriceService: CostCenterPriceService,
    private formBuilder: FormBuilder,
    private storeService: StoreService,
    private transferConstructionSiteDetailService: TransferConstructionSiteDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.transferConstructionSiteDetailContract);
    this.getConceptInvoicingAlquiler();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (this.disabled) { setTimeout(() => this.formTransferConstructionSiteDetail.disable()); }
    const transferConstructionSiteDetails = changes["transferConstructionSiteDetails"];
    const transferConstructionSiteDetailContract = changes['transferConstructionSiteDetailContract'];
    const costCenterPriceContract = changes["costCenterPriceContract"];
    if (transferConstructionSiteDetailContract && !transferConstructionSiteDetailContract.firstChange) {
      if (this.transferConstructionSiteDetailContract.transferConstructionSiteDetailId == 0) {
        this.createNewTransferConstructionSiteDetail();
      } else {
        this.transferConstructionSiteDetailId = this.transferConstructionSiteDetailContract.transferConstructionSiteDetailId;
        await this.validateForm(this.transferConstructionSiteDetailContract);
      }
    }
    if (costCenterPriceContract && !costCenterPriceContract.firstChange) {
      await this.getCostCenterPriceRegister(costCenterPriceContract.currentValue);
    }
    if (transferConstructionSiteDetails && !transferConstructionSiteDetails.firstChange) {
      await this.createDetails(this.transferConstructionSiteDetails);
    }
  }

  clearForm() {
    this.stock = 0;
    this.rateDiscount = 0;
    this.baseUnitValue = 0;
    this.unitValueClient = 0;
    this.codeStore = '';
    this.codeNameArticle = '';
    this.validateForm(new TransferConstructionSiteDetailModel());
    this.isFormView.emit(false);
  }

  async createAndUpdateTransferDetail(detail: TransferConstructionSiteDetailModel) {
    if (this.transferConstructionSiteDetailId == 0) {
      await firstValueFrom(this.transferConstructionSiteDetailService.createTransferConstructionSiteDetail(detail));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue creado exitosamente.`);
    } else {
      await firstValueFrom(this.transferConstructionSiteDetailService.updateTransferConstructionSiteDetail(detail));
      this.toastHandlerService.showToast('success', `El detalle del traslado fue modificado exitosamente.`);
    }
    this.clearForm();
    this.updateListDetail.emit(true);
  }

  createCostCenterPrice() {
    const costCenterPriceRegister = new CostCenterPriceModel();
    costCenterPriceRegister.conceptInvoicingId = this.rentalInvoicingConceptId;
    costCenterPriceRegister.articleId = this.transferConstructionSiteDetailContract.articleId;
    costCenterPriceRegister.costCenterId = this.transferConstructionSiteContract.destinyCostCenterId;
    this.costCenterPriceRegister.emit(costCenterPriceRegister);
  }

  async createCostCenterPriceDestinyByOrigin(originCostCenterId: number, destinityCostCenterId: number, conceptInvoicingId: number, articleId: number) {
    try {
      const response = await firstValueFrom(this.transferConstructionSiteDetailService.createCostCenterPriceDestinyByOrigin(originCostCenterId, destinityCostCenterId, conceptInvoicingId, articleId));
      if (response && response.costCenterPriceId != 0) {
        this.createAndUpdateTransferDetail(this.transferConstructionSiteDetailContract);
      }
    } catch (error) {
      this.disableButtonSave = true;
      console.error('Error al crear el precio por centro de costos destino.', error);
      throw error;
    }
  }

  async createDetails(transferConstructionSiteDetails: TransferConstructionSiteDetailModel[]): Promise<void> {
    try {
      await firstValueFrom(this.transferConstructionSiteDetailService.createDetails(transferConstructionSiteDetails,
        this.transferConstructionSiteContract.transferConstructionSiteId));
      this.toastHandlerService.showToast('success', 'Los detalles del traslado fueron creados exitosamente.', '');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles del traslado.', error);
    }
  }

  async createNewTransferConstructionSiteDetail() {
    this.transferConstructionSiteDetailId = 0;
    this.costCenterPriceContract = new CostCenterPriceModel();
    await this.validateForm(this.transferConstructionSiteDetailContract);
  }

  async getArticle(articleId: number | null) {
    try {
      if (articleId != null && articleId != 0) {
        const article = await firstValueFrom(this.articleService.getArticleById(articleId));
        this.codeNameArticle = `${article.code} - ${article.name}`;
        this.articleId = articleId;
      } else {
        this.articleId = 0;
        this.codeNameArticle = '';
      }
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
    }
  }

  async getConceptInvoicingAlquiler() {
    try {
      const response = await firstValueFrom(this.conceptInvoicingService.getConceptInvoicingAlquiler());
      this.rentalInvoicingConceptId = response.conceptInvoicingId;
      this.conceptInvoicingIdEmit.emit(response.conceptInvoicingId);
    } catch (error) {
      console.error('Error al obtener el consepto de alquiler.', error);
    }
  }

  async getCostCenterPriceRegister(costCenterPriceContract: CostCenterPriceModel) {
    if (costCenterPriceContract.costCenterPriceId == 0) { return; }
    try {
      this.createAndUpdateTransferDetail(this.transferConstructionSiteDetailContract);
    } catch (error) {
      console.error('Error al guardar el detalle.', error);
    }
  }

  async getCostCenterPriceByKeys(costCenterId: number, articleId: number) {
    try {
      if (!costCenterId || !articleId) {
        this.disableButtonSave = true;
        return;
      }
      const response = await firstValueFrom(this.costCenterPriceService.getCostCenterPriceByKeys(costCenterId,
        this.rentalInvoicingConceptId, articleId));
      if (response.costCenterPriceId != 0 && response.baseUnitValue != -1) {
        //Existe en precios por centro de costo con precio válido
        this.disableButtonSave = false;
        this.unitValueClient = response.unitValue;
        this.rateDiscount = response.rateDiscount;
        this.baseUnitValue = response.baseUnitValue;
        this.costCenterPriceContract = response;
      } else if (response.costCenterPriceId == 0 && response.baseUnitValue != -1) {
        //NO existe en precios CCO pero SÍ en lista de precios
        this.disableButtonSave = false;
        this.rateDiscount = 0;
        this.baseUnitValue = 0;
        this.unitValueClient = 0;
        this.costCenterPriceContract = response;
      } else {
        //Existe en CCO pero sin precio base válido
        this.disableButtonSave = true;
        this.rateDiscount = 0;
        this.baseUnitValue = 0;
        this.unitValueClient = 0;
        this.toastHandlerService.showToast('error', 'El artículo NO se encuentra registrado en la lista de Precios.');
      }
    } catch (error) {
      this.disableButtonSave = true;
      console.error('Error el precio por centro de costos.', error);
      throw error;
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView.emit(isFormView);
  }

  async getStore(storeId: number | null) {
    try {
      if (storeId != null && storeId != 0) {
        const store = await firstValueFrom(this.storeService.getStoreById(storeId));
        this.storeId = storeId;
        this.codeStore = store.code.toString();
      } else {
        this.storeId = 0;
        this.codeStore = '';
      }
    } catch (error) {
      this.storeId = 0;
      this.codeStore = '';
      console.error('Error al obtener el bodega.', error);
    }
  }

  async getStockInCostCenterByArticleStore(articleId: number, storeId: number): Promise<void> {
    try {
      const isValidInput = articleId > 0 && storeId > 0;
      if (!isValidInput) {
        this.stock = 0;
        return;
      }
      this.stock = await firstValueFrom(this.transferConstructionSiteDetailService.getStockInCostCenterByArticleStore(
        this.transferConstructionSiteContract, articleId, storeId));
    } catch (error) {
      this.stock = 0;
      console.error('Error al obtener el stock en el centro de costos.', error);
    }
  }

  async getQuantity(quantity: number): Promise<boolean> {
    if (quantity > this.transferConstructionSiteDetailContract.quantity + this.stock!) {
      this.toastHandlerService.showToast('error', `Esta cantidad genera negativos en las existencias del c.costos
      de origen en el período del traslado.`, 'Error');
      return false;
    }
    this.transferConstructionSiteDetailContract.quantity = quantity;
    return true;
  }

  async send() {
    if (this.disabled) {
      this.toastHandlerService.showToast('warning', `Existen cambios sin guardar en el registro del encabezado.
      Por favor, guarde los cambios e inténtelo de nuevo.`, 'Atención');
      return;
    }
    this.validateFormControls();
    if (this.formTransferConstructionSiteDetail.valid) {
      this.formTransferConstructionSiteDetail.enable();
      const modelTransferConstructionSiteDetail = this.formTransferConstructionSiteDetail.value as TransferConstructionSiteDetailModel;
      if (!(await this.getQuantity(modelTransferConstructionSiteDetail.quantity))) { return; }
      try {
        this.costCenterPriceContract = await firstValueFrom(this.costCenterPriceService.getCostCenterPriceByKeys(
          this.transferConstructionSiteContract.destinyCostCenterId, this.rentalInvoicingConceptId,
          modelTransferConstructionSiteDetail.articleId));
        if (this.costCenterPriceContract.costCenterPriceId == 0) {
          if (this.accessCostCenterPrice) {
            this.createCostCenterPrice();
          } else {
            await this.createCostCenterPriceDestinyByOrigin(this.transferConstructionSiteContract.originCostCenterId,
              this.transferConstructionSiteContract.destinyCostCenterId, this.rentalInvoicingConceptId, modelTransferConstructionSiteDetail.articleId);
          }
        } else {
          this.createAndUpdateTransferDetail(this.transferConstructionSiteDetailContract);
        }
      } catch (error) {
        console.error('Error al guardar el detalle.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'La Cantidad mínima permitida es 1.',
        max: 'La Cantidad no puede superar 9.999.999,99.',
      },
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formTransferConstructionSiteDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateFormControls() {
    Object.keys(this.formTransferConstructionSiteDetail.controls).forEach((key) => {
      const control = this.formTransferConstructionSiteDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  async validateForm(transferConstructionSiteDetail: TransferConstructionSiteDetailModel) {
    this.formTransferConstructionSiteDetail = this.formBuilder.group({
      transferConstructionSiteDetailId: [transferConstructionSiteDetail.transferConstructionSiteDetailId, []],
      transferConstructionSiteId: [this.transferConstructionSiteContract.transferConstructionSiteId,
      [Validators.required, Validators.min(1)]],
      articleId: [transferConstructionSiteDetail.articleId, [Validators.required, Validators.min(1)]],
      storeId: [transferConstructionSiteDetail.storeId, [Validators.required, Validators.min(1)]],
      quantity: [transferConstructionSiteDetail.quantity, [Validators.required, Validators.min(1),
      Validators.max(9999999.99)]],
    });
    const { storeId, articleId } = transferConstructionSiteDetail;
    const { destinyCostCenterId } = this.transferConstructionSiteContract;
    if (transferConstructionSiteDetail.articleId > 0) {
      this.isFormView.emit(true);
      await Promise.all([
        this.getStore(storeId),
        this.getArticle(articleId),
        this.getStockInCostCenterByArticleStore(articleId, storeId),
        this.getCostCenterPriceByKeys(destinyCostCenterId, articleId)
      ]);
    }
  }
}
